import { useTranslation } from "@luckymenu/i18n/react";
import {
	parseSupportFragment,
	type SupportSession,
	useCurrentMembership,
	useSetSupportSession,
	useSupportSession,
} from "@luckymenu/staff-core";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@luckymenu/ui-web";
import { useNavigate } from "@tanstack/react-router";
import { LifeBuoy } from "lucide-react";
import { useEffect } from "react";
import { Centered } from "./centered";

/** sessionStorage keeps a support session through a reload of the same tab, and no longer. */
const KEY = "luckymenu.support";

export function readStoredSupport(): SupportSession | null {
	try {
		const raw = sessionStorage.getItem(KEY);
		return raw ? (JSON.parse(raw) as SupportSession) : null;
	} catch {
		return null;
	}
}

export function storeSupport(session: SupportSession | null): void {
	try {
		if (session) sessionStorage.setItem(KEY, JSON.stringify(session));
		else sessionStorage.removeItem(KEY);
	} catch {
		// No storage: the session lasts until the page is reloaded.
	}
}

/**
 * `/support#r=<restaurantId>&t=<token>` from the admin app. Saves the session
 * even before sign-in (Google sign-in reloads the page), and once signed in
 * starts it and removes the token from the address bar.
 */
export function useSupportLink(pathname: string, signedIn: boolean) {
	const setSupport = useSetSupportSession();
	const navigate = useNavigate();
	useEffect(() => {
		if (pathname !== "/support") return;
		const fromLink = parseSupportFragment(window.location.hash);
		if (fromLink) storeSupport(fromLink);
		if (!signedIn) return;
		const session = fromLink ?? readStoredSupport();
		if (session) setSupport(session);
		void navigate({ to: "/orders", replace: true, hash: "" });
	}, [pathname, signedIn, setSupport, navigate]);
}

/** Ends the session and returns to the person's own restaurants. */
export function useEndSupport(): () => void {
	const setSupport = useSetSupportSession();
	const navigate = useNavigate();
	return () => {
		storeSupport(null);
		setSupport(null);
		void navigate({ to: "/", replace: true });
	};
}

/** Shown across the top of every screen during a support session. */
export function SupportBanner() {
	const { t } = useTranslation("staff");
	const support = useSupportSession((s) => s.session);
	const current = useCurrentMembership();
	const end = useEndSupport();
	if (!support) return null;
	return (
		<div
			role="status"
			className="mb-4 flex flex-wrap items-center gap-3 rounded-card border border-highlight bg-highlight/15 px-4 py-3 text-sm"
		>
			<LifeBuoy className="size-5 shrink-0" aria-hidden />
			<p className="min-w-0 flex-1 basis-60">
				{t("support.banner", { restaurant: current.data?.restaurant.name ?? "" })}
			</p>
			<Button variant="outline" size="sm" onClick={end}>
				{t("support.end")}
			</Button>
		</div>
	);
}

export function SupportEndedScreen() {
	const { t } = useTranslation("staff");
	const end = useEndSupport();
	return (
		<Centered>
			<Card>
				<CardHeader>
					<CardTitle>
						<h1 className="text-xl">{t("support.endedTitle")}</h1>
					</CardTitle>
					<CardDescription>{t("support.endedBody")}</CardDescription>
				</CardHeader>
				<CardContent>
					<Button className="w-full" onClick={end}>
						{t("support.close")}
					</Button>
				</CardContent>
			</Card>
		</Centered>
	);
}
